/**
 * Minimal IndexedDB helper for MY PORTAL offline queue.
 * Stores address drafts and pending Quick Find scans, replays them
 * to the backend when the browser is back online.
 */

const DB_NAME = "myportal";
const DB_VERSION = 1;
const STORE_ADDRESS_QUEUE = "address_queue";
const STORE_SCAN_QUEUE = "scan_queue";
const STORE_CACHE = "response_cache";

let dbPromise = null;

function openDB() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (!("indexedDB" in window)) return reject(new Error("IndexedDB not supported"));
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_ADDRESS_QUEUE))
        db.createObjectStore(STORE_ADDRESS_QUEUE, { keyPath: "id" });
      if (!db.objectStoreNames.contains(STORE_SCAN_QUEUE))
        db.createObjectStore(STORE_SCAN_QUEUE, { keyPath: "id" });
      if (!db.objectStoreNames.contains(STORE_CACHE))
        db.createObjectStore(STORE_CACHE, { keyPath: "key" });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

async function tx(store, mode = "readonly") {
  const db = await openDB();
  return db.transaction(store, mode).objectStore(store);
}

// ---------- Address queue (manual add offline) ----------
export async function queueAddress(payload, files = []) {
  const id = `addr-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const filesB64 = await Promise.all(files.map(async (f) => ({
    name: f.name, type: f.type, size: f.size,
    dataUrl: await fileToDataUrl(f),
  })));
  const store = await tx(STORE_ADDRESS_QUEUE, "readwrite");
  await promisifyReq(store.put({ id, payload, files: filesB64, queued_at: Date.now() }));
  return id;
}

export async function listAddressQueue() {
  const store = await tx(STORE_ADDRESS_QUEUE);
  return promisifyReq(store.getAll());
}

export async function removeAddressFromQueue(id) {
  const store = await tx(STORE_ADDRESS_QUEUE, "readwrite");
  return promisifyReq(store.delete(id));
}

// ---------- Scan queue (Quick Find label photo taken offline) ----------
export async function queueScan(file, meta = {}) {
  const id = `scan-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const dataUrl = await fileToDataUrl(file);
  const store = await tx(STORE_SCAN_QUEUE, "readwrite");
  await promisifyReq(store.put({
    id, file: { name: file.name, type: file.type, size: file.size, dataUrl },
    meta, queued_at: Date.now(),
  }));
  return id;
}

export async function listScanQueue() {
  const store = await tx(STORE_SCAN_QUEUE);
  return promisifyReq(store.getAll());
}

export async function removeScanFromQueue(id) {
  const store = await tx(STORE_SCAN_QUEUE, "readwrite");
  return promisifyReq(store.delete(id));
}

// ---------- Response cache (last successful list of addresses) ----------
export async function cachePut(key, value) {
  const store = await tx(STORE_CACHE, "readwrite");
  return promisifyReq(store.put({ key, value, updated_at: Date.now() }));
}

export async function cacheGet(key) {
  const store = await tx(STORE_CACHE);
  const rec = await promisifyReq(store.get(key));
  return rec?.value ?? null;
}

// ---------- helpers ----------
function promisifyReq(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

export function dataUrlToBlob(dataUrl) {
  const [meta, b64] = dataUrl.split(",");
  const mime = /:(.*?);/.exec(meta)?.[1] || "application/octet-stream";
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mime });
}
